import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { HeroSlide } from '@/types/api'
import { ChevronDown, Edit, EyeOff, GripVertical, Plus, Trash2 } from 'lucide-react'
import React from 'react'

interface HeroSlideListProps {
  className?: string
  heroSlides: HeroSlide[]
  isLoading?: boolean
  onAddSlide: () => void
  onEditSlide: (_slide: HeroSlide) => void
  onDeleteSlide: (_slideId: string) => void
  onToggleStatus: (_slideId: string) => void
}

export const HeroSlideList: React.FC<HeroSlideListProps> = ({
  className,
  heroSlides,
  isLoading = false,
  onAddSlide,
  onEditSlide,
  onDeleteSlide,
  onToggleStatus,
}) => {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Hero Slides</CardTitle>
          <Button onClick={onAddSlide}>
            <Plus className="h-4 w-4 mr-2" />
            Add Slide
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading slides...</div>
        ) : heroSlides.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No hero slides found. Click "Add Slide" to create one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {heroSlides.map((slide) => (
              <Card key={slide.id} className="overflow-hidden">
                <div className="relative aspect-video bg-gray-100">
                  {slide.background_image ? (
                    <img
                      src={slide.background_image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        ;(e.target as HTMLImageElement).src =
                          'https://placehold.co/800x400?text=No+Image'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <Badge variant={slide.is_active ? 'default' : 'secondary'}>
                      {slide.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/50 text-white px-2 py-1 rounded text-xs">
                    <GripVertical className="h-3 w-3" />
                    Order: {slide.order}
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg truncate">
                      {slide.title}
                      {slide.title_highlight && (
                        <span className="text-primary-600 ml-1">{slide.title_highlight}</span>
                      )}
                    </h3>
                    {slide.subtitle && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{slide.subtitle}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {slide.primary_cta_text && (
                        <Badge variant="outline">{slide.primary_cta_text}</Badge>
                      )}
                      {slide.secondary_cta_text && (
                        <Badge variant="outline">{slide.secondary_cta_text}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          Actions
                          <ChevronDown className="h-4 w-4 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEditSlide(slide)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onToggleStatus(slide.id)}>
                          <EyeOff className="h-4 w-4 mr-2" />
                          {slide.is_active ? 'Set Inactive' : 'Set Active'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDeleteSlide(slide.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
